import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { connect } from 'react-redux'

import BattleRoomsChannel from 'channels/battlerooms'
import { requestAvailableUnits } from 'state/actions/available_units'
import { requestAvailablePeripheries } from 'state/actions/available_peripheries'
import { getReactiveUpdateUrl } from 'helpers/reactivity'

import UpdateButton from 'components/UpdateButton/UpdateButton'
import UnitCheckboxFilter from '../../components/UnitCheckboxFilter/UnitCheckboxFilter'
import UnitsList from '../../components/UnitsList/UnitsList'
import Squad from '../../components/Squad/Squad'
import BattleRoomsWrapper from '../../components/BattleRoomsWrapper/BattleRoomsWrapper'

import LoadingScreen from 'components/LoadingScreen/LoadingScreen'

import {
    BATTLE_ROOMS_UNITS_LIST,
    REFRESH_UNITS_LIST_TOOLTIP_BODY,
    REFRESH_UNITS_LIST_TOOLTIP_TITLE,
    BATTLE_ROOMS_UNIT_DISABLED,
} from '../../constants'

import BattleRoomsListFilter from '../../components/BattleModeFilter/containers/BattleRoomsListFilter'
import BattleRoomsSchedule from '../../components/BattleRoomsSchedule/BattleRoomsSchedule'

import { UnitDataPropTypes } from '../../propTypes'

import styles from './UnitsView.scss'
import { isUnitDisabled as checkIsUnitDisabled } from '../../components/UnitsList/helpers'

const COOLDOWN = 3000

export class UnitsView extends React.Component {
    static propTypes = {
        clanId: PropTypes.number.isRequired,
        filter: PropTypes.object.isRequired,
        loaded: PropTypes.bool.isRequired,
        participantsError: PropTypes.bool.isRequired,
        pushStreamUrl: PropTypes.string,
        requestAvailableUnits: PropTypes.func.isRequired,
        requestAvailablePeripheries: PropTypes.func.isRequired,
        selectedUnit: UnitDataPropTypes,
        showLoader: PropTypes.bool.isRequired,
        showSchedule: PropTypes.bool.isRequired,
        availablePeripheries: PropTypes.array.isRequired,
    }

    state = { updateCooldown: false }

    constructor(props) {
        super()

        if (props.pushStreamUrl) this.updateChannel(props.pushStreamUrl)
    }

    componentWillMount() {
        this.props.requestAvailableUnits({ showLoader: !this.props.loaded })
        this.props.requestAvailablePeripheries()
    }

    componentWillUnmount() {
        this.updateChannel(null)
        if (this.updateCooldown) clearTimeout(this.updateCooldown)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.pushStreamUrl !== this.props.pushStreamUrl) {
            this.updateChannel(this.props.pushStreamUrl)
        }
    }

    updateChannel(url) {
        if (this.channel) {
            this.channel.disconnect()
        }
        if (url) {
            this.channel = new BattleRoomsChannel(url)
        }
    }

    updateList = () => {
        this.props.requestAvailableUnits()

        this.setState({ updateCooldown: true })
        this.updateCooldown = setTimeout(() => {
            this.setState({ updateCooldown: false })
        }, COOLDOWN)
    }


    renderSelectedUnitView() {
        const { participantsError, selectedUnit, clanId, availablePeripheries } = this.props

        const isUnitSelected = this.props.selectedUnit !== undefined

        if (!isUnitSelected) {
            const viewClassNames = classNames(styles.view, styles.view__empty)
            return <div className={viewClassNames} />
        }
        const isUnitDisabled = participantsError ||
            checkIsUnitDisabled(selectedUnit, clanId) ||
            !availablePeripheries.includes(selectedUnit.periphery.id)

        if (isUnitDisabled) {
            return (
                <div className={classNames(styles.view, styles.view__empty)}>
                    <p className={styles.inactive}>{BATTLE_ROOMS_UNIT_DISABLED}</p>
                </div>
            )
        }

        return (
            <div className={styles.view}>
                <Squad
                    unitData={this.props.selectedUnit}
                    forPreview={false}
                />
            </div>
        )
    }

    render() {
        if (this.props.showLoader) {
            return <LoadingScreen type='fullscreen' />
        }

        let body
        if (this.props.showSchedule) {
            const mainClassName = classNames(styles.main, styles.main__fullWidth)
            body = (
                <div className={styles.body}>
                    <div className={mainClassName}>
                        <BattleRoomsListFilter />
                    </div>
                    <section className={styles.battleRoomsScheduleWrapper}>
                        <BattleRoomsSchedule
                            unitType={this.props.filter.unitType}
                            vehicleLevel={this.props.filter.level}
                        />
                    </section>
                </div>
            )
        } else {
            body = (
                <div className={styles.body}>
                    <div className={styles.main}>
                        <BattleRoomsListFilter />
                        <div>
                            <h2 className={styles.subtitle}>
                                {BATTLE_ROOMS_UNITS_LIST}
                                <div className={styles.updateButton}>
                                    <UpdateButton
                                        handleClick={this.updateList}
                                        disabled={this.state.updateCooldown}
                                        tooltipTitle={REFRESH_UNITS_LIST_TOOLTIP_TITLE}
                                        tooltipMessage={REFRESH_UNITS_LIST_TOOLTIP_BODY}
                                    />
                                </div>
                            </h2>
                            <div className={styles.unitCheckboxFilter}>
                                <UnitCheckboxFilter />
                            </div>
                        </div>
                        <div className={styles.list}>
                            <UnitsList
                                filter={this.props.filter}
                                updateListHandler={this.updateList}
                                updateCooldown={this.state.updateCooldown}
                            />
                        </div>
                    </div>
                    {this.renderSelectedUnitView()}
                </div>
            )
        }

        return (
            <BattleRoomsWrapper>
                {body}
            </BattleRoomsWrapper>
        )
    }
}

export const mapStateToProps = state => {
    const {
        filter,
        loaded,
        meta,
        showLoader,
        selectedUnitId,
        units,
    } = state.availableUnits
    const availablePeripheries = [...state.availablePeripheries.ids, state.account.periphery_id]

    const { id: clanId, token: reactiveClanToken } = state.clan

    const { pushstream_prefix } = meta
    const { pushStreamHost } = state.settings

    const unitChannels = []
    units.forEach(unit => {
        const publicUnitChannel = `${pushstream_prefix}${unit.id}`
        unitChannels.push(publicUnitChannel)

        if (unit.clan.id === clanId) {
            const clanUnitChannel = `${publicUnitChannel}${reactiveClanToken}`
            unitChannels.push(clanUnitChannel)
        }
    })
    const unitChannelsUrl = unitChannels.join('/')
    const pushStreamUrl = getReactiveUpdateUrl(pushStreamHost, unitChannelsUrl)

    const selectedUnit = units.find(unit => unit.id === selectedUnitId)
    const participantsError = state.unitParticipants.error

    return {
        clanId,
        filter,
        loaded,
        participantsError,
        pushStreamUrl,
        selectedUnit,
        showLoader,
        availablePeripheries,
    }
}

const mapDispatchToProps = {
    requestAvailableUnits,
    requestAvailablePeripheries,
}

export default connect(mapStateToProps, mapDispatchToProps)(UnitsView)



// WEBPACK FOOTER //
// ./containers/BattleRooms/containers/UnitsView/UnitsView.jsx